import {IBasicParams, IdName, IMessageData, TBaseOrObservableType, TRouteId} from "../../index";
import * as ko from "knockout";
import PromiseRequest from "../../req/PromiseRequest";
import {global} from "../../appContext";
import {TServiceName, TServiceParameters} from "../../constants/ApiServices";
import {REQUEST_MESSAGE} from "../../constants/Language";
import {createErrorMessage, getAnyValue, isNullOrUndefined} from "../../util/misc";
import {getHash, getRouteDefinition, getRouteLabel} from "../../constants/RouteDefinitions";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {nsFa} from "../fa/fn";
import i = nsFa.i;
import VariableAbstractHtml from './VariableAbstract.html?raw';

export const template: string = VariableAbstractHtml;

export interface IVariableParams<T> extends Partial<IBasicParams> {
    id?: TBaseOrObservableType<number>
    item?: TBaseOrObservableType<T>
    linkable?: boolean
    noClickBubble?: boolean
    orgnr?: string
    requestMessage?: IMessageData
    showIcon?: boolean
    showNoData?: boolean
    target?: string
    spinnerOnly?: boolean
}

export abstract class VariableAbstract<T extends IdName> {
    protected id!: number;
    protected item!: TBaseOrObservableType<T>;
    protected linkable: boolean;
    protected messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    protected name!: string;
    protected noClickBubble: boolean
    protected req!: PromiseRequest;
    protected showIcon: boolean;
    protected target: string | null;
    protected isReady: KnockoutObservable<boolean | null> = ko.observable(null)
    protected tt: string | null = null
    protected icon: string | null = null
    protected href: string | null = null
    protected abstract serviceName: TServiceName
    protected abstract routeId: TRouteId
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker()
    protected showNoData: boolean
    protected readonly html: KnockoutObservable<string | null> = ko.observable(null)

    constructor(protected params: IVariableParams<T>) {
        this.showNoData = params.showNoData ?? true
        this.linkable = params.linkable ?? false
        this.showIcon = params.showIcon ?? false
        this.target = params.target ?? null
        this.noClickBubble = params.noClickBubble ?? false

        requestAnimationFrame(() => {
            if (params.item) {
                this.onSuccess(params.item)
            } else if (params.id) {
                this.id = ko.unwrap(params.id)
                this.req = global.getPromiseRequest(this.serviceName);
                this.request();
            }

            if (ko.isObservable(params.item)) {
                this.kosub.subscribe(params.item, () => {
                    if (params.item !== undefined) {
                        this.onSuccess(params.item)
                    }
                })
            }
            if (ko.isObservable(params.id)) {
                this.kosub.subscribe(params.id, () => {
                    if (params.id !== undefined) {
                        this.id = ko.unwrap(params.id)
                        this.request()
                    }
                })
            }
        })
    }

    protected request() {
        this.isReady(false)
        if (!this.id) {
            return
        }
        this.initRequestMessage()
        this.getRequest()
            .then((data: T[]) => {
                this.html(null)
                if (!data) {
                    throw 'no data'
                }
                this.messageData(null);
                const item: T = this.getItemFromArray(data)
                this.onSuccess(item)
            })
            .catch((reason: string) => {
                this.html(null)
                if (this.showNoData) {
                    this.messageData(null)
                } else {
                    this.messageData(createErrorMessage(String(reason)));
                }
            });
    }

    protected initRequestMessage() {
        if (this.params.spinnerOnly) {
            this.html(i('fa-spin fa-spinner fa-rw'))
        } else {
            this.messageData(this.params.requestMessage ?? {message: REQUEST_MESSAGE});
        }
    }

    protected getRequest(): Promise<any> {
        return this.req.request(this.getParams())
    }

    protected getParams(): TServiceParameters {
        return {id: this.id}
    }

    protected getItemFromArray(data: T[]): T {
        if (data.length > 1) {
            return data.find((item: T) => item.id == this.id) ?? data[0]!;
        }
        return data[0]!;
    }

    protected onSuccess(item: TBaseOrObservableType<T>) {
        this.item = item
        this.id = getAnyValue(this.item).id
        this.name = getAnyValue(this.item).name
        this.href = getHash(this.routeId, getAnyValue(this.item), true, this.params.orgnr)
        const def = getRouteDefinition(this.routeId)
        if (def) {
            if (isNullOrUndefined(this.icon)) {
                this.icon = def.icon ?? null
            }
            if (isNullOrUndefined(this.tt)) {
                this.tt = getRouteLabel(def)
            }
        }
        this.isReady(true)
    }

    protected getTitle(): string {
        return this.tt ?? ''
    }

    dispose() {
        this.req?.abort();
        this.kosub.unsuscribeAll()
    }

}
